import React from 'react'
import { useTheme } from '@ui-kitten/components'
import { useSelector } from 'react-redux'
import FastImage, { ImageStyle } from 'react-native-fast-image'
import { useNavigation } from '@react-navigation/native'
import moment from 'moment'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import { StyleService, useStyleSheet } from '@src/style/service'
import { TouchableSection } from '@src/components'
import {
  allEhrAppointmentsSelector,
  topPriorityCoachAssignmentSelector,
} from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'
import { userSelector } from '@src/selectors/app'
import { EhrAppointmentStateKind, SurveysConfigKind, SurveysUserLinkState } from '@src/types'
import { useIsDarkMode } from '@src/config/theme'
import { EhrAppointmentsQuery } from '@src/screens/NutritionistHub/graphql/ehrAppointments.generated'
// eslint-disable-next-line max-len
import { useShowWeightLossRecurringCallsBanner } from '@src/components/InformationPopups/hooks/useShowWeightLossRecurringCallsBanner'
import Storage from '@src/utils/storage'
import { useOnboardingExperienceBanner } from '../WeightLossExperience/useBanner'

export const FirstCallQuestionnaireBanner = () => {
  const theme = useTheme()
  const styles = useStyleSheet(themedStyle)
  const isDarkMode = useIsDarkMode()
  const navigation = useNavigation()
  const notificationInboxEnabled = useFeatureFlag(Feature.NotificationInbox)

  const coachAssignment = useSelector(topPriorityCoachAssignmentSelector)
  const user = useSelector(userSelector)
  const allEhrAppointments = useSelector(allEhrAppointmentsSelector)

  const {
    shouldShowBanner: showOnboardingExperienceBanner,
    handleOnPress: handleOnboardingExperiencePress,
  } = useOnboardingExperienceBanner()
  const { showBanner: showWeightLossRecurrentCallsBanner } = useShowWeightLossRecurringCallsBanner()

  const nutritionistAvatar = coachAssignment?.coach?.coachProfile?.photo
  const surveyLinks = user?.surveyLinks || []
  const isGoalsQuestionnaireCompleted = surveyLinks.some(
    (surveyLink) =>
      surveyLink.survey.kind === SurveysConfigKind.Goals &&
      surveyLink.finished &&
      surveyLink.state !== SurveysUserLinkState.Stopped,
  )
  const firstScheduledOrOccurredAppointment = allEhrAppointments
    .filter((appointment) =>
      [
        EhrAppointmentStateKind.Occurred,
        EhrAppointmentStateKind.Scheduled,
        EhrAppointmentStateKind.InReview,
      ].includes(appointment.lastState.kind),
    )
    .sort((a, b) => moment(a.meetingAt).valueOf() - moment(b.meetingAt).valueOf())[0] as
    | EhrAppointmentsQuery['ehrAppointments']['appointments'][0]
    | undefined
  const goalsQuestionnaireTapped = Storage.get(
    `${Storage.FIRST_CALL_BANNER_GOALS_QUESTIONNAIRE_TAPPED_KEY}_${user?.id}`,
  )

  if (notificationInboxEnabled) {
    return null
  }

  if (
    showWeightLossRecurrentCallsBanner ||
    (goalsQuestionnaireTapped && !showOnboardingExperienceBanner)
  ) {
    return null
  }

  // We only want to show the banner if
  // - User scheduled their first appointment
  // - Onboarding not completed
  // - Goals questionnaire not completed
  // If the user had their first appointment occurred, we want to keep the banner
  // for 24 hours after the appointment

  // If no first appointment exists, don't show banner
  if (!firstScheduledOrOccurredAppointment) {
    return null
  }

  // If the appointment has occurred, check if it's within 24 hours
  if (firstScheduledOrOccurredAppointment.lastState.kind === EhrAppointmentStateKind.Occurred) {
    // If more than 24 hours have passed since the appointment occurred, don't show banner
    if (moment().diff(moment(firstScheduledOrOccurredAppointment.meetingAt), 'hours') > 24) {
      return null
    }
  }

  // Don't show banner if onboarding is completed AND goals questionnaire is completed
  if (!showOnboardingExperienceBanner && isGoalsQuestionnaireCompleted) {
    return null
  }

  const handleOnPress = () => {
    if (showOnboardingExperienceBanner) {
      handleOnboardingExperiencePress()
      return
    }

    Storage.set(`${Storage.FIRST_CALL_BANNER_GOALS_QUESTIONNAIRE_TAPPED_KEY}_${user?.id}`, true)

    return navigation.navigate('Questionnaire', {
      questionnaire: SurveysConfigKind.Goals,
    })
  }

  const textColor = isDarkMode ? theme['theme.tooltip.textPrimary'] : theme['theme.text.primary']
  const backgroundColor = isDarkMode
    ? theme['theme.marketing.yellow']
    : theme['theme.marketing.lightYellow']
  const rightArrowColor = isDarkMode ? theme['theme.solid.black'] : undefined

  return (
    <TouchableSection
      title="Get ready for your first call!"
      subtitle="Fill out the questionnaire to personalize your journey!"
      onPress={handleOnPress}
      style={[styles.content, { backgroundColor }]}
      subtitleStyle={[styles.subtitle, { color: textColor }]}
      labelStyle={{ color: textColor }}
      accessoryLeft={
        nutritionistAvatar ? (
          <FastImage
            source={{ uri: nutritionistAvatar }}
            style={styles.nutritionistAvatar as ImageStyle}
          />
        ) : undefined
      }
      showRightArrow
      rightArrowColor={rightArrowColor}
    />
  )
}

const themedStyle = StyleService.create({
  content: {
    borderRadius: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
  },
  nutritionistAvatar: {
    height: 56,
    width: 56,
    borderRadius: 28,
    marginRight: 16,
  },
  subtitle: {
    maxWidth: 246,
  },
})
